<template>
  <div id="floorplan-detail" :class="{ 'ca-plan': isCaPlan }">
    <MoleculesLoadingOverlay v-if="loading" />
    <div v-else>
      <OrganismsProductDetailBW :organism="brandWindow" />
      <OrganismsFlexibleFeatures
        v-if="flexibleFeatures.item && flexibleFeatures.item.length"
        :organism="flexibleFeatures"
      />
      <OrganismsTabbedContent
        v-if="tabbedContent.defaultTabHash"
        :organism="tabbedContent"
      >
        <template v-for="item in tabbedContent.item" :key="item.id" #[item.id]>
          <img
            class="mx-auto mb-5"
            :src="item.image.src"
            :alt="item.image.alt"
          />
          <MoleculesButtons
            :buttons="tabbedContent.button"
            :button-colors="tabbedContent.button.colors"
            class="!justify-center mx-auto"
          />
          <AtomsAltadenaDisclaimer v-if="isCaPlan" class="mt-8 mb-16" />
        </template>
      </OrganismsTabbedContent>
      <OrganismsGalleryGrid
        id="featured-photos-gallery"
        v-if="galleryGrid.gallery.length"
        class="mx-auto my-24"
        :organism="galleryGrid"
      />
      <div v-if="alternatingContent.length" class="alternating-content-sections">
        <OrganismsSplitContent
          v-for="(item, index) in alternatingContent"
          :key="index"
          :organism="item"
        />
      </div>
      <div
        v-if="viewSlideshow.slideshowEmbed"
        class="px-2 pt-24 pb-12 mx-auto bg-bc-gray view-a-slideshow"
      >
        <h2
          v-if="viewSlideshow.title1"
          class="mb-8 text-2xl text-center md:text-3xl lg:text-5xl theme-text-2xl"
        >
          {{ viewSlideshow.title1 }}
        </h2>
        <div class="pb-16" v-html="viewSlideshow.slideshowEmbed"></div>
        <!--<MoleculesButtons
          :buttons="viewSlideshow.buttons"
          :button-colors="viewSlideshow.buttons.buttonColors"
          class="uppercase"
        />-->
      </div>
      <OrganismsVirtualTour
        id="virtual-tour"
        v-if="virtualTour.virtualTourUrl"
        class="mx-auto my-24 bg-gray-900"
        :organism="virtualTour"
      />
      <OrganismsPopularHomes v-if="popularHomes.items.length" :organism="popularHomes" pageType="plan" :dynamicName="planName"/>
      <OrganismsTextCTA id="cta-be-the-first" :organism="beTheFirst" />
      <OrganismsModalForm>
        <header class="mb-4">
          <h2 class="mb-4 text-4xl font-semibold text-black font-archer">
            Be the First
          </h2>
          <p v-if="!isFormSubmitted" class="text-lg text-gray-500">
            Please provide us your information and we will get back to you.
            Check 'Become a Boulder Creek Insider' to receieve updates regarding
            this plan.
          </p>
        </header>
        <FormsHubSpotForm @submit="isFormSubmitted = true" :form="form" />
      </OrganismsModalForm>
      <OrganismsPopularPlans
        v-if="popularPlans.items.length"
        :organism="popularPlans"
      />
      <!-- CA (Altadena) plans carry the licensing disclaimer under the brand
           window and floorplan tabs instead, so they skip the gradient. -->
      <div
        v-if="!isCaPlan"
        class="block h-24 bg-gradient-to-b from-bc-gray to-white"
      ></div>
    </div>
  </div>
</template>

<style>
/* CA (Altadena) plans: hide footer content under the mountain graphic,
   matching rebuilding-altadena-ca.vue */
body:has(#floorplan-detail.ca-plan) {
  .site-footer > div {
    display: none !important;
  }
}

#floorplan-detail {
  .text-cta {
    @apply mb-20;
    p {
      @apply leading-8;

      a {
        @apply text-sm text-bc-dark-blue underline;
      }
    }

    .buttons a {
      @apply uppercase;
    }
  }

  .virtual-tour {
    h2 {
      @apply text-white;
    }
  }

  .tabbed-content {
    header {
      background-image: linear-gradient(101deg, #367ea0 0%, #4dadce 97%);
      .header-content {
        @apply max-w-[556px];
      }
    }
    .tabs-component-tabs {
      @apply mx-auto flex-col md:flex-row justify-center;

      .tabs-component-tab {
        @apply md:max-w-[190px];

        .tabs-component-tab-a {
          @apply whitespace-nowrap;
        }
      }
    }
    .tabs-component-panels {
      .buttons a {
        @apply text-[15px] uppercase font-ubuntu font-medium py-3 px-10;
      }
    }
  }

  .popular-plans, .popular-homes {
    .product-grid {
      @apply w-full;
    }
  }

  .split-content {
    @apply sm:mb-20 mb-10 py-0;
  }

  .alternating-content-sections .split-content:last-child {
    @apply mb-0 pb-0 sm:mb-0 sm:pb-0;
  }

  /* Match the Featured Photos Gallery's width and gutters */
  .alternating-content-sections .split-content .content {
    @apply px-4 max-w-7xl;
  }

  .view-a-slideshow {
    iframe {
      @apply mx-auto w-[1038px] max-w-full h-80 sm:h-[696px] overflow-hidden;
    }
    .buttons {
      @apply justify-center;
    }
  }
  .virtual-tour {
    @apply my-0;
    h2 {
      @apply text-white;
    }
  }
}
</style>

<script setup>
const route = useRoute();
import { useAsyncQuery } from "#imports";
import { GET_PLAN } from "~/graphql/GetPlan";
// Explicit, stable key so the SSR/ISR payload is reused on hydration instead
// of refetching (the auto key hashes a reactive proxy that differs between the
// Node prerender and the browser). Key off route.params, NOT route.path:
// route.path differs by a trailing slash between the prerender and the browser,
// which would reintroduce the mismatch. See community index.vue for the story.
const { data, pending, error } = await useAsyncQuery({
  query: GET_PLAN,
  variables: { uri: route.path },
  key: `plan-detail:${route.params.location}/${route.params.community}/${route.params.plan}`,
});

// A plan flagged "Plan Not Available" stays published (so its child specs
// remain in the third-party feeds) but must not be reachable on the front end.
// A direct URL hit bypasses all list-level filtering, so 404 it here.
if (isPlanHidden(data?.value?.plan)) {
  throw createError({
    statusCode: 404,
    statusMessage: "Page Not Found",
    fatal: true,
  });
}

import { storeToRefs } from "pinia";
const breadcrumbs = useBreadcrumbs();
const { breadcrumbsList } = storeToRefs(breadcrumbs);
const modalForm = useModalForm();
const isFormSubmitted = ref(false);

const loading = pending;

const plan = computed(() => {
  return data?.value?.plan ?? [];
});

const plans = computed(() => {
  return data?.value?.plans?.nodes ?? [];
});

const planName = computed(() => {
  return plan?.value?.floorplanDetails?.displayTitle;
});

const homes = computed(() => {
  return data?.value?.homes?.nodes ?? [];
});

// Get the collection of the model
const planCollection = computed(() => {
  return plan.value?.floorplanDetails?.collection;
});

// Plans flagged "Altadena Floorplan" are the California (Altadena rebuild)
// plans. These swap the Colorado copy, HubSpot form, and footer for the CA
// equivalents.
const isCaPlan = computed(() => {
  return plan.value?.floorplanDetails?.altadenaFloorplan ?? false;
});

const planTitle = computed(() => {
  return plan.value?.title;
});

// Filter homes based on the model's collection
const filteredHomes = computed(() => {
  return homes.value.filter(home => home?.qmiHomeDetails?.floorplan?.title === planTitle.value);
});

//console.log(filteredHomes.value);

// Shuffle and limit to 3 items
const popularHomes = computed(() => {
  const shuffledHomes = filteredHomes.value.sort(() => 0.5 - Math.random());
  return {
    backgroundColor: "#ffffff",
    items: shuffledHomes.slice(0, 3),
  };
});

//console.log("popularHomes", popularHomes);

const collectionFeatures = computed(() => {
  let parsedJSON = {};
  try {
    parsedJSON = data?.value?.collectionFeatures
      ? JSON.parse(data?.value?.collectionFeatures)
      : {};
  } catch (e) {
    // console.log("Invalid collectionFeatures JSON:", e);
    return {};
  }
  return parsedJSON;
});

const computedBreadcrumbs = computed(() => {
  return [
    {
      title: plan?.value?.floorplanDetails?.neighborhood?.title
        ? plan?.value?.floorplanDetails?.neighborhood?.title +
          ", " +
          plan?.value?.floorplanDetails?.neighborhood?.neighborhood?.city
        : "",
      link: plan?.value?.floorplanDetails?.neighborhood?.uri,
    },
    {
      title: "Floorplans",
      link: plan?.value?.floorplanDetails?.neighborhood?.uri + "#floorplans",
    },
    {
      title: plan?.value?.title + " Plan",
    },
  ];
});

watch(computedBreadcrumbs, () => {
  breadcrumbsList.value = computedBreadcrumbs.value;
});

const brochureLink = computed(() => {
  const pdf = plan?.value?.floorplanDetails?.pdfEPub;
  return typeof pdf === "string" && pdf.trim() ? pdf.trim() : "";
});

const brandWindow = computed(() => {
  return {
    collection: plan?.value?.floorplanDetails?.collection,
    // Plans flagged "Altadena Floorplan" are built under license, so the
    // collection button credits the licensor instead of the collection.
    collectionTitle: plan?.value?.floorplanDetails?.altadenaFloorplan
      ? "Foothill Catalog Foundation"
      : undefined,
    title: plan?.value?.floorplanDetails?.displayTitle,
    // title: plan?.value?.title,
    type: plan?.value?.floorplanDetails?.type,
    neighborhoodTitle: plan?.value?.floorplanDetails?.neighborhood?.title,
    neighborhoodLink: plan?.value?.floorplanDetails?.neighborhood?.uri,
    bedsMin: plan?.value?.floorplanDetails?.bedroomsMin,
    bedsMax: plan?.value?.floorplanDetails?.bedroomsMax,
    bathsMin: plan?.value?.floorplanDetails?.bathroomsMin,
    bathsMax: plan?.value?.floorplanDetails?.bathroomsMax,
    garageMin: plan?.value?.floorplanDetails?.garageMin,
    garageMax: plan?.value?.floorplanDetails?.garage,
    sqftMin: plan?.value?.floorplanDetails?.mainSqrFt,
    sqftMax: plan?.value?.floorplanDetails?.finishedSqrFt,
    priceMin: plan?.value?.floorplanDetails?.basePrice,
    productStatus: plan?.value?.productStatus,
    altadenaFloorplan: plan?.value?.floorplanDetails?.altadenaFloorplan ?? false,
    autoplaySlider: true,
    autoplayDelay: 10000,
    sliderImages: plan?.value?.floorplanDetails?.elevations,
    button1: !!brochureLink.value,
    button1Text: "Get Brochure",
    button1Target: "_blank",
    link1: brochureLink.value,
    id: plan?.value?.databaseId,
    thumbnail: getImageSrc(
      plan?.value?.floorplanDetails?.elevations[0],
      "medium",
    ),
  };
});

const tabbedContent = computed(() => {
  const tabs = Array.isArray(plan?.value?.floorplanDetails?.tabs)
    ? plan.value.floorplanDetails.tabs.map((tab, currentIndex, array) => {
        let tabName = tab.title;

        array.forEach(function (checkTab, checkIndex, checkArray) {
          if (tab.title === checkTab.title) {
            if (currentIndex > checkIndex) {
              tabName = tab.title + " - Opt";
            }
          }
        });

        const adjustedIndex = currentIndex + 1;

        return {
          name: tabName,
          id: kebabCase(tabName) + "-tab-" + adjustedIndex,
          image: {
            src: getImageSrc(tab.image, "large"),
            alt: tab.image.altText,
          },
        };
      })
    : [];

  return {
    containerWidth: "default",
    title1: "Explore this floorplan",
    title2: plan?.value?.title,
    item: tabs,
    defaultTabHash: tabs[0]?.id,
    variant: "product-detail",
    button: {
      colors: ["button-yellow"],
      button1: !!brochureLink.value,
      button1Text: "Get Brochure",
      button1Target: "_blank",
      link1: brochureLink.value,
    },
  };
});

const beTheFirst = computed(() => {
  return {
    title1: "Be the First",
    description: `<p>Be the first to know about our ${isCaPlan.value ? "California" : "Colorado"} neighborhoods and homes, including early access to pricing, new home plans and VIP events.</p>`,
    button1: true,
    button1Text: "Get Updates",
  };
});

const flexibleFeatures = computed(() => {
  const collection = plan?.value?.floorplanDetails?.collection;
  let collectionTitle = "";
  let featuresList = [];

  switch (true) {
    case collection?.toLowerCase().includes("easyhouse"):
      collectionTitle = "easyHouse®";
      featuresList = collectionFeatures?.value?.easyHouse ?? [];
      break;
    case collection?.toLowerCase().includes("deed-restricted-wc"):
      collectionTitle = "Deed Restricted wee-Cottage®";
      featuresList = collectionFeatures?.value?.deedRestrictedWC ?? [];
      break;
    case collection?.toLowerCase().includes("wee-cottage"):
      collectionTitle = "wee-Cottage®";
      featuresList = collectionFeatures?.value?.weeCottage ?? [];
      break;
    case collection?.toLowerCase().includes("limited-edition"):
      collectionTitle = "Limited Edition";
      featuresList = collectionFeatures?.value?.limitedEdition ?? [];
      break;
    case collection?.toLowerCase().includes("new-beginnings"):
      collectionTitle = "New Beginnings";
      featuresList = collectionFeatures?.value?.newBeginnings ?? [];
      break;
  }

  return {
    title1: collectionTitle + " Features",
    item: featuresList,
  };
});

const popularPlans = computed(() => {
  // Check if specific floorplans are provided
  const specificFloorplans = plan?.value?.floorplanDetails?.popularPlans ?? [];

  // If specific floorplans exist, use them; otherwise, use the default logic.
  // Either way, drop any plan flagged "Plan Not Available".
  const items = specificFloorplans.length > 0
    ? specificFloorplans.filter((p) => !isPlanHidden(p))
    : getPopularPlans(plan?.value, plans?.value);

  return {
    backgroundColor: "#f0f0f0",
    items,
  };
});

const galleryGrid = computed(() => {
  const galleryItems = Array.isArray(
    plan?.value?.floorplanDetails?.galleryItems,
  )
    ? plan?.value?.floorplanDetails?.galleryItems
        .map((item) => item?.image)
        .filter(Boolean)
    : [];

  return { gallery: galleryItems, thumbnailCount: 10, backgroundColor: "#fff" };
});

const alternatingContent = computed(() => {
  const rows = plan.value?.floorplanDetails?.plansAlternatingContent;
  return Array.isArray(rows)
    ? rows.map((item, index) => {
        return {
          backgroundColor: index % 2 ? "#f0f0f0" : "#ffffff",
          splitContentLayout: index % 2 ? "image-text" : "text-image",
          title1: item.plansSplitTitle,
          description: item.plansSplitDescription,
          button1: item.plansSplitButton,
          button1Text: item.plansSplitButtonText,
          link1: item.plansSplitButtonUrl,
          image: item.plansSplitImage,
          fullBleed: true,
        };
      })
    : [];
});

const virtualTour = computed(() => {
  const collection = plan?.value?.floorplanDetails?.collection ?? "";
  
  // Format collection name for display
  let collectionDisplay = collection;
  if (collection?.includes("deed-restricted-wc")) {
    collectionDisplay = "Deed Restricted wee-Cottage®";
  }
  
  const collectionSpace = collectionDisplay ? collectionDisplay + " " : "";

  const showButton1 = false;

  return {
    title1: plan?.value?.floorplanDetails?.matterportTourTitle,
    title2: "Tour this " + collectionSpace + " home",
    virtualTourEmbedType: "url",
    virtualTourUrl: plan?.value?.floorplanDetails?.matterportTour,
    button1: showButton1,
    button1Text: "Learn More",
    link1: "#null",
    buttonColors: ["button-gray"],
  };
});

const viewSlideshow = computed(() => {
  const showButton1 = !!brochureLink.value;
  return {
    title1: plan?.value?.floorplanDetails?.matterportTourTitle
      ? "Step inside the " + plan?.value?.floorplanDetails?.matterportTourTitle
      : "",
    slideshowEmbed: plan?.value?.floorplanDetails?.virtualTour,
    buttons: {
      button1: showButton1,
      button1Text: "Get Brochure",
      button1Target: "_blank",
      link1: brochureLink.value,
      buttonColors: ["button-yellow"],
    },
  };
});

onMounted(() => {
  window.addEventListener(
    "hashchange",
    () => {
      if (window.location.hash === "#modal-form") {
        modalForm.openModal();
        window.location.hash = "";
      }
    },
    false,
  );

  addTriggers();
});

const addTriggers = () => {
  const modalFormTriggers = document.querySelectorAll(
    "#cta-be-the-first .button-1, #cta-be-the-first a[href='#modal-form']",
  );

  modalFormTriggers.forEach((node) => {
    // Clone & replace node to remove existing event listeners
    const clonedNode = node.cloneNode(true);
    node.parentNode.replaceChild(clonedNode, node);

    clonedNode.addEventListener("click", (event) => {
      // Add event listener
      event.preventDefault();
      modalForm.openModal();
    });
  });
};

onUpdated(() => {
  addTriggers();
});

// HubSpot "Be the First" form IDs. CA form is pending - swap in the real ID
// once it exists; until then CA plans fall back to the Colorado form.
const HUBSPOT_FORM_ID_CO = "ca2c18ce-092f-4156-aecd-257fb7776d83";
const HUBSPOT_FORM_ID_CA = "0b9fb411-b311-4a6d-9c48-b81996a5f6b0";

const form = computed(() => {
  return {
    id: isCaPlan.value ? HUBSPOT_FORM_ID_CA : HUBSPOT_FORM_ID_CO,
    hiddenFields: [
      {
        inputSelector: 'input[name="floorplan_of_interest"]',
        inputValue: plan?.value?.floorplanDetails?.hubspotValue ?? "",
      },
    ],
  };
});

watch(loading, async (newLoading, oldLoading) => {
  const urlHash = window.location.hash;

  if (urlHash && oldLoading && !newLoading) {
    await nextTick();
    document.querySelector(urlHash).scrollIntoView(true);
  }
});
</script>
